/**
 * 登录回跳地址解析
 * 站内所有「未登录 → 登录页」的跳转统一走这里，保证 redirect 参数的口径一致：
 * 1. 兼容 hash 路由（redirect 既支持页面完整地址，也支持 `/coupon/xxx?claim=1` 这类 fullPath）
 * 2. 只放行 tushare.pro 主域/子域与当前站点同源地址，防止开放重定向
 * 3. 参数缺失或非法时回退站点根，避免登录后跳转到空白页
 */

/**
 * 当前站点的部署路径（用于拼登录回跳地址）
 * 兼容 `/`、`/home/`、`/home/index.html` 等部署方式，
 * 直接用 location.pathname 推导，避免写死 publicPath 导致回跳地址错误
 */
export function currentBasePath() {
  if (typeof window === "undefined" || !window.location) return "/";
  const path = window.location.pathname || "/";
  // 形如 /home/index.html 时只保留目录部分
  return /\.html?$/i.test(path) ? path.replace(/[^/]*$/, "") : path;
}

/**
 * 把 hash 路由的 fullPath（如 `/coupon/ABCDEFGHIJKL?claim=1`）拼成站点完整地址
 * 注意：hash 模式下 `origin + fullPath` 会丢失 `#`，必须显式补上
 */
function toAbsoluteHashUrl(fullPath) {
  const raw = String(fullPath);
  if (!raw.startsWith("/") || raw.startsWith("//")) return "";
  const basePath = currentBasePath().replace(/\/+$/, "");
  return `${window.location.origin}${basePath}#${raw}`;
}

/**
 * 解析登录后的回跳地址
 * @param {string} raw 登录 URL 上的 redirect 参数（完整地址或站内 fullPath）
 * @param {string} [fallback] 无参数/非法地址时的兜底地址，默认站点根
 * @returns {string} 可直接交给 window.location.replace 的完整地址
 */
export function resolveLoginRedirect(raw, fallback) {
  const fallbackUrl = fallback || window.location.origin;
  if (typeof raw !== "string" || !raw) return fallbackUrl;

  const candidate = toAbsoluteHashUrl(raw) || raw;
  try {
    const url = new URL(candidate, window.location.origin);
    const host = url.hostname;
    const hostAllowed =
      host === "tushare.pro" ||
      host.endsWith(".tushare.pro") ||
      url.origin === window.location.origin;
    if (hostAllowed && (url.protocol === "https:" || url.protocol === "http:")) {
      return url.href;
    }
  } catch (e) {
    /* 非法地址静默忽略，回退兜底地址 */
  }
  return fallbackUrl;
}

/**
 * 把 query 对象拼成 `?a=1&b=2`（忽略空值，保留原始编码）
 */
function toQueryString(params) {
  const search = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null) return;
    search.append(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

/**
 * OAuth 登录页「绑定手机号成功」后的回跳地址
 * 与登录成功后的跳转口径保持一致：优先使用 redirect 携带的授权页地址，
 * 否则用其余授权参数重新拼出 /oauth/authorize，保证授权上下文不丢失
 * @param {object} query 登录页路由 query
 * @returns {string} 可直接交给 window.location.replace 的完整地址
 */
export function resolveOAuthBindRedirect(query) {
  const { redirect, ...rest } = query || {};
  const target =
    typeof redirect === "string" && redirect.startsWith("/oauth/")
      ? redirect
      : `/oauth/authorize${toQueryString(rest)}`;
  return resolveLoginRedirect(target, window.location.origin);
}
